<script>
  import AdminNav from '@/components/admin/AdminNav.svelte';
  import { router } from '@inertiajs/svelte'

  let { users = [], pagination = {}, filters = {}, deleted_count = 0, expiring_soon_count = 0, is_super_admin = false, flash = {} } = $props()

  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let searchQuery = $state(filters?.q || '')
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let statusFilter = $state(filters?.status || '')
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let roleFilter = $state(filters?.role || '')
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let companyVerificationStatusFilter = $state(filters?.company_verification_status || '')

  let isDeletedView = $derived(statusFilter === 'deleted')

  function applyFilters() {
    const params = {}
    if (searchQuery) params.q = searchQuery
    if (statusFilter) params.status = statusFilter
    if (roleFilter) params.role = roleFilter
    if (companyVerificationStatusFilter) params.company_verification_status = companyVerificationStatusFilter
    router.get('/admin/users', params, { preserveState: true })
  }

  function goToPage(page) {
    router.get('/admin/users', { ...filters, page }, { preserveState: true })
  }

  function getRoleBadgeColor(role) {
    switch (role) {
      case 'super_admin': return 'badge-error'
      case 'admin': return 'badge-primary'
      case 'moderator': return 'badge-info'
      default: return 'badge-neutral'
    }
  }

  function getStatusBadgeColor(status) {
    switch (status) {
      case 'active': return 'badge-success'
      case 'pending': return 'badge-warning'
      case 'banned': return 'badge-error'
      case 'dormant': return 'badge-neutral'
      default: return 'badge-neutral'
    }
  }

  function getCompanyStatusBadgeColor(status) {
    switch (status) {
      case 'verified': return 'badge-success'
      case 'restricted': return 'badge-error'
      default: return 'badge-neutral'
    }
  }

  function getCompanyStatusLabel(status) {
    switch (status) {
      case 'verified': return '인증됨'
      case 'restricted': return '이용제한'
      default: return '미인증'
    }
  }

  function restoreUser(id) {
    router.post(`/admin/users/${id}/restore`)
  }

  function permanentDeleteUser(id) {
    if (confirm('이 사용자를 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      router.delete(`/admin/users/${id}/permanent_delete`)
    }
  }

  function getDaysUntilExpiry(discardedAt) {
    if (!discardedAt) return null
    const discardDate = new Date(discardedAt)
    const expiryDate = new Date(discardDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    const now = new Date()
    const days = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
    return Math.max(0, days)
  }
</script>

<AdminNav activeTab="users" />

  <main>
    {#if flash?.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    {#if flash?.alert}
      <div class="mb-6 p-4 bg-error-light dark:bg-error/20 border border-error/30 rounded-xl text-error-dark dark:text-error text-body2">
        {flash.alert}
      </div>
    {/if}

    <div class="flex items-center justify-between mb-6">
      <h2 class="text-h1 text-content">사용자 관리</h2>
      <span class="text-body2 text-content-tertiary">총 {pagination.total_count || 0}명</span>
    </div>

    <!-- Filters -->
    <div class="card card-bordered p-4 mb-6 shadow-md">
      <div class="flex flex-wrap items-center gap-4">
        {#if !isDeletedView}
          <input
            type="text"
            bind:value={searchQuery}
            placeholder="이름 또는 이메일 검색..."
            class="input flex-1 min-w-[200px] py-2.5"
          />
        {/if}

        <select bind:value={statusFilter} class="select">
          <option value="">모든 상태</option>
          <option value="active">활성</option>
          <option value="pending">대기중</option>
          <option value="banned">정지됨</option>
          <option value="dormant">휴면</option>
          <option value="deleted">삭제됨 ({deleted_count})</option>
        </select>

        {#if !isDeletedView}
          <select bind:value={roleFilter} class="select">
            <option value="">모든 역할</option>
            <option value="super_admin">최고관리자</option>
            <option value="admin">관리자</option>
            <option value="moderator">모더레이터</option>
            <option value="user">일반 회원</option>
          </select>

          <select bind:value={companyVerificationStatusFilter} class="select">
            <option value="">회사 인증 전체</option>
            <option value="verified">인증됨</option>
            <option value="unverified">미인증</option>
            <option value="restricted">이용제한</option>
          </select>
        {/if}

        <button onclick={applyFilters} class="btn btn-primary btn-md">검색</button>
      </div>
    </div>

    <!-- Deleted Warning Banner -->
    {#if isDeletedView}
      <div class="mb-6 p-4 bg-warning-light dark:bg-warning/20 border border-warning/30 rounded-xl text-warning-dark dark:text-warning text-body2">
        삭제된 사용자는 30일 후 자동 영구삭제됩니다.
        {#if expiring_soon_count > 0}
          <span class="ml-2 font-semibold">(23일+ 경과: {expiring_soon_count}명)</span>
        {/if}
      </div>
    {/if}

    <!-- Users Table -->
    <div class="card card-bordered overflow-hidden shadow-md">
      <table class="w-full">
        <thead class="bg-surface-tertiary">
          <tr>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">사용자</th>
            {#if isDeletedView}
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">역할</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">삭제일</th>
              <th class="px-6 py-3 text-center text-caption font-semibold text-content-tertiary uppercase">만료</th>
            {:else}
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">역할</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">상태</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">인증</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">회사 인증</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">가입일</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">세션</th>
            {/if}
            <th class="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each users as user}
            <tr class="hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors {isDeletedView ? 'opacity-75' : 'cursor-pointer'}"
              onclick={() => { if (!isDeletedView) router.get(`/admin/users/${user.id}`) }}
              role={isDeletedView ? undefined : 'button'}
              tabindex={isDeletedView ? undefined : '0'}
              onkeydown={(e) => { if (!isDeletedView && e.key === 'Enter') router.get(`/admin/users/${user.id}`) }}
            >
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-primary-50 dark:bg-dark-tertiary rounded-full flex items-center justify-center text-primary dark:text-dark-text-high font-semibold">
                    {user.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div class="text-body1 font-medium text-content">{user.name}</div>
                    <div class="text-caption text-content-tertiary">{user.email}</div>
                    {#if user.organization}
                      <div class="text-caption text-content-tertiary">{user.organization}</div>
                    {/if}
                  </div>
                </div>
              </td>
              {#if isDeletedView}
                <td class="px-6 py-4">
                  <span class={`badge ${getRoleBadgeColor(user.role)}`}>{user.role}</span>
                </td>
                <td class="px-6 py-4 text-body2 text-content-tertiary">
                  {user.discarded_at ? new Date(user.discarded_at).toLocaleDateString('ko-KR') : '-'}
                </td>
                <td class="px-6 py-4 text-center">
                  {#if user.discarded_at}
                    {@const days = getDaysUntilExpiry(user.discarded_at)}
                    <span class="text-caption font-medium {days <= 7 ? 'text-error' : 'text-warning-dark dark:text-warning'}">
                      {days}일 남음
                    </span>
                  {/if}
                </td>
              {:else}
                <td class="px-6 py-4">
                  <span class={`badge ${getRoleBadgeColor(user.role)}`}>{user.role}</span>
                </td>
                <td class="px-6 py-4">
                  <span class={`badge ${getStatusBadgeColor(user.status)}`}>{user.status}</span>
                </td>
                <td class="px-6 py-4">
                  {#if user.verified}
                    <span class="text-success font-medium">&#10003; 인증됨</span>
                  {:else}
                    <span class="text-content-tertiary">미인증</span>
                  {/if}
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-col gap-1">
                    <span class={`badge ${getCompanyStatusBadgeColor(user.company_verification_status)}`}>
                      {getCompanyStatusLabel(user.company_verification_status)}
                    </span>
                    <span class="text-caption text-content-tertiary font-mono">{user.company_domain || '-'}</span>
                  </div>
                </td>
                <td class="px-6 py-4 text-body2 text-content-tertiary">
                  {new Date(user.created_at).toLocaleDateString('ko-KR')}
                </td>
                <td class="px-6 py-4 text-body2 text-content-tertiary">
                  {user.sessions_count || 0}
                </td>
              {/if}
              <td class="px-6 py-4 text-right">
                {#if isDeletedView}
                  <div class="flex items-center justify-end gap-2">
                    <button onclick={(e) => { e.stopPropagation(); restoreUser(user.id) }} class="text-success hover:text-success-dark text-body2 font-medium">복구</button>
                    {#if is_super_admin}
                      <button onclick={(e) => { e.stopPropagation(); permanentDeleteUser(user.id) }} class="text-error hover:text-error-dark text-body2 font-medium">영구삭제</button>
                    {/if}
                  </div>
                {:else}
                  <a href={`/admin/users/${user.id}`} class="text-primary hover:text-primary-dark text-body2 font-medium">상세보기</a>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if users.length === 0}
        <div class="text-center py-12 text-content-tertiary">
          {isDeletedView ? '삭제된 사용자가 없습니다' : '검색 결과가 없습니다'}
        </div>
      {/if}
    </div>

    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-6">
        <button onclick={() => goToPage(pagination.current_page - 1)} disabled={pagination.current_page === 1} class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed">이전</button>
        <span class="px-4 py-2 text-body2 text-content-tertiary">{pagination.current_page} / {pagination.total_pages}</span>
        <button onclick={() => goToPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.total_pages} class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed">다음</button>
      </div>
    {/if}
  </main>
